import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, Check, X, RotateCcw, EyeOff, Eye } from "lucide-react";
import { apiClient, useAuth } from "@/lib/auth";
import ArrangeGrid from "@/components/nds/ArrangeGrid";

const cache = {};

/** Saved order + hidden cards for one board (one layout for everyone). */
export function useBoardLayout(board) {
  const [lay, setLay] = useState(cache[board] || { order: [], hidden: [] });
  useEffect(() => {
    apiClient.get(`/card-layouts/${board}`).then(({ data }) => { cache[board] = data; setLay(data); }).catch(() => {});
  }, [board]);
  const save = async (next) => {
    const { data } = await apiClient.put(`/card-layouts/${board}`, next);
    cache[board] = data; setLay(data);
  };
  return [lay, save];
}

const sortBy = (ids, order) => {
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...ids].sort((a, b) => (rank.has(a) ? rank.get(a) : 1e6 + ids.indexOf(a)) - (rank.has(b) ? rank.get(b) : 1e6 + ids.indexOf(b)));
};

/**
 * Any card screen: renders `ids` in the saved order (hidden ones skipped). Super admins get "Arrange cards":
 * drag to move, X to hide, a "Hidden" tray to bring cards back, Reset to the built-in order. Saved for everyone.
 */
export default function ArrangeBoard({ board, ids, labels = {}, renderTile, gridClass = "grid-cols-[repeat(auto-fill,minmax(220px,1fr))]", testid, label = "cards", defaultHidden = [], onShown, hiddenLabel = "Hidden" }) {
  const [saved, save] = useBoardLayout(board);
  const lay = saved.saved ? saved : { order: saved.order || [], hidden: defaultHidden };
  const { user } = useAuth();
  const sa = user?.role === "super_admin";
  const [draft, setDraft] = useState(null);
  const hidden = new Set(draft?.hidden || lay.hidden || []);
  const ordered = sortBy(ids, draft?.order || lay.order || []);
  const move = (from, to) => setDraft((d) => { const o = sortBy(ids, d.order); const i = o.indexOf(from); const j = o.indexOf(to); o.splice(i, 1); o.splice(j, 0, from); return { ...d, order: o }; });
  const addBack = (id) => setDraft((d) => ({ order: [...sortBy(ids, d.order).filter((x) => x !== id), id], hidden: d.hidden.filter((x) => x !== id) }));
  const done = async () => {
    try { await save(draft); toast.success("Card layout saved for everyone"); setDraft(null); } catch { toast.error("Could not save the layout"); }
  };
  const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border font-mono text-[0.6rem] tracking-[0.16em] uppercase transition-colors";
  const shown = ordered.filter((id) => !hidden.has(id));
  const shownKey = shown.join(",");
  useEffect(() => { onShown?.(shownKey ? shownKey.split(",") : []); }, [shownKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const tray = (draft?.hidden || []).filter((id) => ids.includes(id));
  return (
    <div className="space-y-3" data-testid={testid || `arrange-board-${board}`}>
      {sa && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`arrange-bar-${board}`}>
          {!draft ? (
            <button type="button" onClick={() => setDraft({ order: ordered, hidden: [...hidden] })} data-testid={`arrange-toggle-${board}`}
              className={`${btn} border-slate-700 text-slate-300 hover:text-brand-cyan hover:border-brand-cyan/60`}><LayoutGrid className="w-3.5 h-3.5" /> Arrange {label}</button>
          ) : (<>
            <button type="button" onClick={done} data-testid={`arrange-done-${board}`} className={`${btn} border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10`}><Check className="w-3.5 h-3.5" /> Done</button>
            <button type="button" onClick={() => setDraft(null)} data-testid={`arrange-cancel-${board}`} className={`${btn} border-slate-700 text-slate-400 hover:text-slate-200`}><X className="w-3.5 h-3.5" /> Cancel</button>
            <button type="button" onClick={() => setDraft({ order: [], hidden: defaultHidden })} data-testid={`arrange-reset-${board}`} className={`${btn} border-slate-700 text-slate-400 hover:text-amber-200`}><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
            <span className="font-mono text-[0.6rem] text-slate-500 uppercase tracking-[0.14em]">Drag to move · {defaultHidden.length ? "Hide" : "X"} to hide · saved for everyone</span>
          </>)}
        </div>
      )}
      {draft ? (<>
        <ArrangeGrid ids={shown} renderTile={renderTile} gridClass={gridClass} onMove={move} removeLabel={defaultHidden.length ? "Hide" : undefined} onRemove={shown.length > 1 ? (id) => setDraft((d) => ({ ...d, hidden: [...d.hidden, id] })) : undefined} />
        {(tray.length > 0 || defaultHidden.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 p-2 rounded-md border border-dashed border-slate-700" data-testid={`arrange-hidden-${board}`}>
            <span className="flex items-center gap-1 font-mono text-[0.6rem] text-slate-500 uppercase tracking-[0.14em]"><EyeOff className="w-3 h-3" /> {hiddenLabel}:</span>
            {!tray.length && <span className="font-mono text-[0.6rem] text-slate-600 uppercase tracking-[0.14em]" data-testid={`arrange-hidden-empty-${board}`}>None</span>}
            {tray.map((id) => (
              <button key={id} type="button" onClick={() => addBack(id)} data-testid={`arrange-show-${board}-${id}`}
                className={`${btn} border-slate-700 text-slate-300 hover:text-brand-cyan`}><Eye className="w-3 h-3" /> {defaultHidden.length ? "Add back · " : ""}{labels[id] || id.replace(/_/g, " ")}</button>))}
          </div>
        )}
      </>) : (
        <div className={`grid ${gridClass} gap-3`}>{shown.map((id) => <React.Fragment key={id}>{renderTile(id)}</React.Fragment>)}</div>
      )}
    </div>
  );
}
